import React from 'react';

interface DocxHtmlPreviewProps {
  htmlContent: string;
  width?: string;
  height?: string;
  className?: string;
}

/**
 * Component to display HTML content rendered from a DOCX file using docx-preview
 * Provides proper styling and layout for the rendered Word document
 */
export const DocxHtmlPreview: React.FC<DocxHtmlPreviewProps> = ({
  htmlContent,
  width = '100%',
  height = '100%',
  className = '',
}) => {
  return (
    <div className={`docx-html-preview ${className}`} style={{ width, height }}>
      <div
        className="docx-content"
        style={{
          // Base styles for proper DOCX rendering
          fontFamily: 'Calibri, Arial, sans-serif',
          fontSize: '11pt',
          lineHeight: 1.15,
          color: '#000000',
          backgroundColor: '#ffffff',
          padding: '20px',
          margin: '0',
          overflow: 'auto',
          height: '100%',
          // Word document page styling
          maxWidth: '210mm', // A4 width
          marginLeft: 'auto',
          marginRight: 'auto',
          boxShadow: '0 0 10px rgba(0,0,0,0.1)',
          border: '1px solid #e0e0e0',
        }}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
      <style>{`
        .docx-html-preview .docx-content p {
          margin: 0 0 8pt 0;
          text-align: justify;
        }
        
        .docx-html-preview .docx-content h1,
        .docx-html-preview .docx-content h2,
        .docx-html-preview .docx-content h3,
        .docx-html-preview .docx-content h4,
        .docx-html-preview .docx-content h5,
        .docx-html-preview .docx-content h6 {
          margin: 12pt 0 6pt 0;
          font-weight: bold;
        }
        
        .docx-html-preview .docx-content table {
          border-collapse: collapse;
          width: 100%;
          margin: 8pt 0;
        }
        
        .docx-html-preview .docx-content td,
        .docx-html-preview .docx-content th {
          border: 1px solid #000000;
          padding: 4pt;
          vertical-align: top;
        }
        
        .docx-html-preview .docx-content ul,
        .docx-html-preview .docx-content ol {
          margin: 0 0 8pt 0;
          padding-left: 20pt;
        }
        
        .docx-html-preview .docx-content li {
          margin: 0 0 4pt 0;
        }
        
        /* Handle page breaks */
        .docx-html-preview .docx-content .page-break {
          page-break-before: always;
          border-top: 1px dashed #ccc;
          margin: 20px 0;
        }
        
        /* Style for document fields/placeholders */
        .docx-html-preview .docx-content .field {
          background-color: #f0f8ff;
          border: 1px dashed #0066cc;
          padding: 2px 4px;
          border-radius: 2px;
          font-style: italic;
        }
        
        /* Responsive adjustments */
        @media (max-width: 768px) {
          .docx-html-preview .docx-content {
            padding: 10px;
            max-width: 100%;
          }
        }
      `}</style>
    </div>
  );
};
